import { colorByPlatform } from "~/feature/chart/constants";
import {
  ChartViewEnum,
  type IMarketGroupChanceHistory,
} from "~/feature/chart/types";
import {
  CONSENSUS_LINE_KEY,
  getMarketLineKey,
  isNoSide,
  sidesByViewMode,
} from "~/feature/chart/utils";
import type { IMarket } from "~/types/event";
import type { PlatformEnum } from "~/types/platform";

type ChancePoint = IMarketGroupChanceHistory["historical"][number];

type MarketChance = ChancePoint["byPlatform"][number];

export enum ChartLineVariantEnum {
  SOLID = "solid",
  DASHED = "dashed",
  DOTTED = "dotted",
  CONSENSUS = "consensus",
}

export interface IChartLine {
  key: string;
  color: string;
  variant: ChartLineVariantEnum;
  data: (number | null)[];
}

export interface IBuildChartLinesParams {
  markets: IMarket[];
  selections: string[];
  chanceHistory: IMarketGroupChanceHistory[];
  timeline: number[];
  viewMode: ChartViewEnum;
  consensusColor: string;
}

const getPoints = (chanceHistory: IMarketGroupChanceHistory[]): ChancePoint[] =>
  chanceHistory.flatMap(({ historical }) => historical);

const getTimestamp = ({ timestamp }: ChancePoint) => Date.parse(timestamp);

const getChancesByTimestamp = (points: ChancePoint[]) => {
  const byTimestamp = new Map<number, Map<string, MarketChance>>();

  for (const point of points) {
    const timestamp = getTimestamp(point);
    const byMarketId =
      byTimestamp.get(timestamp) ?? new Map<string, MarketChance>();

    for (const chance of point.byPlatform) {
      byMarketId.set(chance.marketId, chance);
    }

    byTimestamp.set(timestamp, byMarketId);
  }

  return byTimestamp;
};

const getSideChance = (chance: MarketChance | undefined, isDashed: boolean) => {
  if (!chance) {
    return null;
  }

  return isDashed ? chance.noChance : chance.yesChance;
};

const getConsensusByTimestamp = (points: ChancePoint[]) =>
  new Map(
    points.map((point) => [
      getTimestamp(point),
      point.consensus?.yesChance ?? null,
    ])
  );

export const buildChartLines = ({
  markets,
  selections,
  chanceHistory,
  timeline,
  viewMode,
  consensusColor,
}: IBuildChartLinesParams): IChartLine[] => {
  if (!timeline.length) {
    return [];
  }

  const points = getPoints(chanceHistory);
  const chancesByTimestamp = getChancesByTimestamp(points);
  const isConsensus = viewMode === ChartViewEnum.CONSENSUS;
  const sides = sidesByViewMode[viewMode];

  const marketLines = markets
    .filter(({ id }) => selections.includes(id))
    .flatMap((market) =>
      sides.map((side) => {
        const isDashed = isNoSide(side);
        const variant = isDashed
          ? ChartLineVariantEnum.DASHED
          : isConsensus
            ? ChartLineVariantEnum.DOTTED
            : ChartLineVariantEnum.SOLID;

        return {
          key: getMarketLineKey(market.id, side),
          color: colorByPlatform[market.platform as PlatformEnum],
          variant,
          data: timeline.map((timestamp) =>
            getSideChance(
              chancesByTimestamp.get(timestamp)?.get(market.id),
              isDashed
            )
          ),
        };
      })
    );

  if (!isConsensus) {
    return marketLines;
  }

  const consensusByTimestamp = getConsensusByTimestamp(points);

  return [
    ...marketLines,
    {
      key: CONSENSUS_LINE_KEY,
      color: consensusColor,
      variant: ChartLineVariantEnum.CONSENSUS,
      data: timeline.map(
        (timestamp) => consensusByTimestamp.get(timestamp) ?? null
      ),
    },
  ];
};
